import React, { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useStores } from '../../hooks/use-stores';
import TableServer from '../../components/super/TableServer';
import {
    WebhookStatusChip,
    eventNamesOf,
    webhookScope,
} from '../../components/super/webhooks/webhookFormatters';

const rowsPerPage = 25;

function WebhooksList() {
    const { webhooksStore } = useStores();
    const { webhooks, webhooksCount, loadingWebhooks, webhookQuery } =
        webhooksStore;
    const navigate = useNavigate();

    useEffect(() => {
        webhooksStore.loadWebhooks({ p: 0, n: rowsPerPage });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // TableServer fires this on mount as well as on a real page change, so
    // without the guard the list would load twice.
    const onChangePagingParams = useCallback(
        (pageIdx, pageSize) => {
            const { p, n } = webhooksStore.webhookQuery;
            if (p === pageIdx && n === pageSize) return;
            webhooksStore.loadWebhooks({ p: pageIdx, n: pageSize });
        },
        [webhooksStore]
    );

    const applyFilter = (key) => (event) =>
        webhooksStore.loadWebhooks({ [key]: event.target.value, p: 0 });

    const columns = useMemo(
        () => [
            {
                Header: 'Id',
                accessor: 'id',
                Cell: (x) => <Link to={`/webhooks/${x.value}`}>{x.value}</Link>,
            },
            {
                Header: 'Name',
                accessor: 'name',
                Cell: (x) => x.value || '-',
            },
            {
                Header: 'Url',
                accessor: 'url',
                Cell: (x) => (
                    <span style={{ wordBreak: 'break-all' }}>{x.value}</span>
                ),
            },
            {
                Header: 'Scope',
                id: 'scope',
                accessor: (row) => webhookScope(row),
                disableSortBy: true,
            },
            {
                Header: 'Events',
                id: 'events',
                accessor: (row) => eventNamesOf(row),
                disableSortBy: true,
            },
            {
                Header: 'Company',
                accessor: 'companyId',
                Cell: (x) => x.value || '-',
            },
            {
                Header: 'Partner',
                accessor: 'partnerId',
                Cell: (x) => x.value || '-',
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: (x) => <WebhookStatusChip status={x.value} />,
            },
            {
                Header: 'Created',
                accessor: 'created',
                Cell: (x) => (x.value ? moment(x.value).format('LLL') : '-'),
            },
        ],
        []
    );

    const pagesTotal = Math.max(
        1,
        Math.ceil(webhooksCount / (webhookQuery.n || rowsPerPage))
    );

    return (
        <Grid container direction="row" spacing={2}>
            <Grid item xs={12}>
                <Box
                    mt={4}
                    mb={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography variant="h2">Webhooks</Typography>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => navigate('/webhookEvents')}
                    >
                        Webhook events
                    </Button>
                </Box>

                <Box display="flex" style={{ gap: 16 }} mb={2} flexWrap="wrap">
                    <TextField
                        label="Webhook id"
                        size="small"
                        style={{ minWidth: 120 }}
                        value={webhookQuery.id}
                        onChange={applyFilter('id')}
                    />
                    <TextField
                        label="Search name or url"
                        size="small"
                        style={{ minWidth: 240 }}
                        value={webhookQuery.search}
                        onChange={applyFilter('search')}
                    />
                    <TextField
                        label="Company id"
                        size="small"
                        style={{ minWidth: 120 }}
                        value={webhookQuery.companyId}
                        onChange={applyFilter('companyId')}
                    />
                    <TextField
                        label="Partner id"
                        size="small"
                        style={{ minWidth: 120 }}
                        value={webhookQuery.partnerId}
                        onChange={applyFilter('partnerId')}
                    />
                    <TextField
                        select
                        label="Scope"
                        size="small"
                        style={{ minWidth: 160 }}
                        value={webhookQuery.scope}
                        onChange={applyFilter('scope')}
                    >
                        <MenuItem value="">Any scope</MenuItem>
                        <MenuItem value="company">Company</MenuItem>
                        <MenuItem value="partner">Partner</MenuItem>
                    </TextField>
                    <TextField
                        select
                        label="Status"
                        size="small"
                        style={{ minWidth: 160 }}
                        value={webhookQuery.status}
                        onChange={applyFilter('status')}
                    >
                        <MenuItem value="">Any status</MenuItem>
                        <MenuItem value={1}>Enabled</MenuItem>
                        <MenuItem value={0}>Disabled</MenuItem>
                    </TextField>
                </Box>

                <TableServer
                    columns={columns}
                    data={webhooks.slice()}
                    rowsTotal={webhooksCount}
                    pagesTotal={pagesTotal}
                    loadingData={loadingWebhooks}
                    onChangePagingParams={onChangePagingParams}
                    initialPageSize={rowsPerPage}
                    hidePagination={pagesTotal <= 1}
                />
            </Grid>
        </Grid>
    );
}

export default observer(WebhooksList);
